'use client'

import * as React from 'react'

/**
 * Hook to send iframe height updates to parent window
 * Use this in pages that are embedded in iframes to auto-resize
 */
export function useIframeResize() {
    React.useEffect(() => {
        // Only run if we're in an iframe
        if (typeof window === 'undefined' || window.self === window.top) {
            return
        }

        let timeoutId: NodeJS.Timeout | null = null

        const sendHeight = () => {
            // Clear any pending timeout
            if (timeoutId) {
                clearTimeout(timeoutId)
            }

            // Debounce height updates to avoid excessive messages
            timeoutId = setTimeout(() => {
                // Get the actual content height
                const height = Math.max(
                    document.body.scrollHeight,
                    document.body.offsetHeight,
                    document.documentElement.clientHeight,
                    document.documentElement.scrollHeight,
                    document.documentElement.offsetHeight
                )

                window.parent.postMessage(
                    {
                        type: 'iframe-resize',
                        height,
                    },
                    '*' // In production, you might want to specify the origin
                )
            }, 100) // 100ms debounce
        }

        // Send initial height after a short delay to ensure content is loaded
        const initialTimeout = setTimeout(() => {
            sendHeight()
        }, 300)

        // Send height on resize
        const resizeObserver = new ResizeObserver(() => {
            sendHeight()
        })

        resizeObserver.observe(document.body)

        // Also listen for window resize
        window.addEventListener('resize', sendHeight)

        // Send height when content loads/changes
        const mutationObserver = new MutationObserver(() => {
            sendHeight()
        })

        mutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['style', 'class'], // Only watch for style/class changes
        })

        // Also listen for images loading (common cause of height changes)
        const images = document.querySelectorAll('img')
        images.forEach((img) => {
            if (!img.complete) {
                img.addEventListener('load', sendHeight)
                img.addEventListener('error', sendHeight)
            }
        })

        return () => {
            clearTimeout(initialTimeout)
            if (timeoutId) {
                clearTimeout(timeoutId)
            }
            resizeObserver.disconnect()
            mutationObserver.disconnect()
            window.removeEventListener('resize', sendHeight)
            images.forEach((img) => {
                img.removeEventListener('load', sendHeight)
                img.removeEventListener('error', sendHeight)
            })
        }
    }, [])
}
